import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';

import defaultDoctorImage from '@/assets/images/Doctor_Placeholder@2x.png';
import * as customPropTypes from '@/custom-prop-types';
import { Button } from '@/design-system/atoms/Button';
import { LazyIconCalendar } from '@/design-system/atoms/Icons/IconCalendar/Lazy';
import Typography from '@/design-system/atoms/Typography';

import * as Card from '../components';
import styles from './SearchResultsCardTeam.module.scss';

/**
 * Search Results Card - Medical Team
 * @param {object} props
 * @param {object} props.appointmentsCta - Appointments call to action
 * @param {object} props.badge - Badge label
 * @param {string} props.className - Additional class name
 * @param {string} props.description - Description
 * @param {object} props.image - Image object
 * @param {object} props.link - Link object
 * @param {object} props.status - Status object
 * @param {string} props.title - Title
 * @param {object} props.analytics - Analytics object
 * @param {boolean} props.isInternal - Is internal link
 * @returns
 */
export const SearchResultsCardTeam = React.forwardRef(
    (
        {
            analytics,
            appointmentsCta,
            badge,
            className,
            description,
            image,
            isInternal,
            link,
            status,
            title,
            coveoAnalyticsCallbacks,
            ...props
        },
        ref,
    ) => {
        const defaultImage = {
            src: defaultDoctorImage,
            alt: 'Generic doctor icon',
        };

        const analyticsObj = analytics
            ? {
                  ...analytics,
                  componentTitle: badge?.label,
                  providerName: title,
                  providerSpecialities: description,
              }
            : undefined;

        const handleClick = useCallback(() => {
            coveoAnalyticsCallbacks?.onClick?.();
        }, [coveoAnalyticsCallbacks]);

        const handleContextMenu = useCallback(() => {
            coveoAnalyticsCallbacks?.onContextMenu?.();
        }, [coveoAnalyticsCallbacks]);

        const handleMouseDown = useCallback(() => {
            coveoAnalyticsCallbacks?.onMouseDown?.();
        }, [coveoAnalyticsCallbacks]);

        const handleMouseUp = useCallback(() => {
            coveoAnalyticsCallbacks?.onMouseUp?.();
        }, [coveoAnalyticsCallbacks]);

        const handleTouchStart = useCallback(() => {
            coveoAnalyticsCallbacks?.onTouchStart?.();
        }, [coveoAnalyticsCallbacks]);

        const handleTouchEnd = useCallback(() => {
            coveoAnalyticsCallbacks?.onTouchEnd?.();
        }, [coveoAnalyticsCallbacks]);

        useEffect(() => {
            coveoAnalyticsCallbacks?.cancelPendingSelect?.();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
            <Card.Container
                image={image || defaultImage}
                className={className}
                ref={ref}
                {...props}
            >
                <Card.Header>
                    {badge?.type && badge?.label && (
                        <Card.Badge type={badge.type} label={badge.label} />
                    )}
                </Card.Header>
                <Card.Content>
                    <Card.Title
                        analytics={analyticsObj}
                        isInternal={isInternal}
                        link={link}
                        title={title}
                        onClick={coveoAnalyticsCallbacks ? handleClick : undefined}
                        onContextMenu={coveoAnalyticsCallbacks ? handleContextMenu : undefined}
                        onMouseDown={coveoAnalyticsCallbacks ? handleMouseDown : undefined}
                        onMouseUp={coveoAnalyticsCallbacks ? handleMouseUp : undefined}
                        onTouchEnd={coveoAnalyticsCallbacks ? handleTouchEnd : undefined}
                        onTouchStart={coveoAnalyticsCallbacks ? handleTouchStart : undefined}
                    />
                    <Typography variant="t2" content={description} lineClamp={{ sm: 4, lg: 2 }} />
                    {status && <Card.Status {...status} />}
                    {appointmentsCta && (
                        <div className={styles['appointments']}>
                            <Button
                                buttonStyle={Button.STYLE.TEXT}
                                iconLeft
                                iconComponent={LazyIconCalendar}
                                className={styles['appointments__cta']}
                                fullWidth={{ md: true }}
                                {...appointmentsCta}
                                analytics={analyticsObj}
                            />
                        </div>
                    )}
                </Card.Content>
            </Card.Container>
        );
    },
);

SearchResultsCardTeam.defaultProps = {
    badge: {
        type: 'provider',
        label: 'Medical Team',
    },
};

SearchResultsCardTeam.propTypes = {
    appointmentsCta: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string,
        csr: PropTypes.bool,
        label: PropTypes.string,
        ariaLabel: PropTypes.string,
    }),
    badge: PropTypes.shape({
        type: PropTypes.string,
        label: PropTypes.string,
    }),
    className: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.shape({
        defaultImgSrc: PropTypes.string,
        alt: PropTypes.string,
    }),
    isInternal: PropTypes.bool,
    link: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string,
        csr: PropTypes.bool,
        ariaLabel: PropTypes.string,
    }),
    status: PropTypes.shape({
        label: PropTypes.string,
        isActive: PropTypes.bool,
    }),
    title: PropTypes.string,
    analytics: customPropTypes.analyticsPropType,
};

SearchResultsCardTeam.displayName = 'SearchResultsCardTeam';

export default SearchResultsCardTeam;
